import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'
import { Picture } from '@/components/media/picture'

interface CoverArtProps {
    src: string | null
    alt: string
    seed: string
    className?: string
    sizes?: string
    priority?: boolean
    label?: boolean
    grade?: 'night' | 'dusk' | 'ink' | 'none'
}

const palettes: Array<[string, string, string]> = [
    ['#240d14', '#070606', '#C9A574'],
    ['#0e161b', '#050607', '#A9BCC9'],
    ['#19130b', '#060504', '#D0B07A'],
    ['#10150e', '#050605', '#AEBB95'],
    ['#181020', '#060507', '#C7AECF'],
    ['#151414', '#060606', '#E9E4DA']
]

const hash = (value: string): number => {
    let result = 5381

    for (let index = 0; index < value.length; index += 1) result = ((result << 5) + result + value.charCodeAt(index)) | 0

    return Math.abs(result)
}

const Motif: FC<{ variant: number, accent: string, gradientId: string }> = ({ variant, accent, gradientId }): ReactNode => {
    if (variant === 0) {
        return (
            <g>
                <circle cx='200' cy='168' r='118' fill={`url(#${gradientId})`} opacity='0.5' />
                <circle cx='200' cy='168' r='78' fill={accent} opacity='0.9' />
                <circle cx='232' cy='150' r='72' fill='#080808' opacity='0.92' />
                <circle cx='200' cy='168' r='104' fill='none' stroke={accent} strokeOpacity='0.25' strokeWidth='0.8' />
            </g>
        )
    }

    if (variant === 1) {
        return (
            <g fill='none' stroke={accent}>
                {[34, 56, 78, 100, 122, 144].map((radius, index) => (
                    <circle key={radius} cx='200' cy='180' r={radius} strokeOpacity={0.5 - index * 0.07} strokeWidth='0.9' />
                ))}
                <circle cx='200' cy='180' r='20' fill={accent} fillOpacity='0.85' stroke='none' />
            </g>
        )
    }

    if (variant === 2) {
        return (
            <g fill='none' stroke={accent} strokeLinecap='round'>
                <path d='M196 352 C 188 290, 222 236, 204 176 C 196 150, 206 122, 214 104' strokeWidth='1.6' strokeOpacity='0.85' />
                <path d='M200 300 C 168 286, 150 262, 148 236 C 176 240, 196 262, 200 300 Z' strokeWidth='1.1' strokeOpacity='0.6' />
                <path d='M206 250 C 236 238, 256 214, 258 188 C 232 194, 210 216, 206 250 Z' strokeWidth='1.1' strokeOpacity='0.6' />
                <path d='M203 196 C 180 186, 168 168, 166 148 C 186 152, 200 170, 203 196 Z' strokeWidth='1.1' strokeOpacity='0.5' />
                <circle cx='214' cy='96' r='16' fill={accent} fillOpacity='0.85' stroke='none' />
                <circle cx='214' cy='96' r='30' strokeOpacity='0.35' strokeWidth='0.9' />
            </g>
        )
    }

    if (variant === 3) {
        return (
            <g>
                <circle cx='200' cy='238' r='64' fill={accent} opacity='0.88' />
                <rect x='0' y='238' width='400' height='162' fill='#080808' opacity='0.94' />
                {[248, 262, 278, 296, 318].map((y, index) => (
                    <line key={y} x1={40 + index * 12} x2={360 - index * 12} y1={y} y2={y} stroke={accent} strokeOpacity={0.45 - index * 0.07} strokeWidth='1' />
                ))}
            </g>
        )
    }

    return (
        <g>
            {[0, 1, 2, 3, 4].map(index => {
                const x = 80 + index * 60
                const offset = [-14, -8, 0, 8, 14][index]

                return (
                    <g key={index}>
                        <circle cx={x} cy='190' r='18' fill={accent} opacity={index === 2 ? 0.95 : 0.8} />
                        {index !== 2 && <circle cx={x + offset} cy='190' r='18' fill='#080808' opacity='0.95' />}
                    </g>
                )
            })}
            <line x1='60' x2='340' y1='236' y2='236' stroke={accent} strokeOpacity='0.3' strokeWidth='0.8' />
        </g>
    )
}

const Placeholder: FC<{ seed: string, alt: string, label: boolean }> = ({ seed, alt, label }): ReactNode => {
    const id = useId().replace(/:/g, '')
    const value = hash(seed || 'serene-corpse')
    const [from, to, accent] = palettes[value % palettes.length]
    const variant = Math.floor(value / 7) % 5

    return (
        <span role='img' aria-label={alt} className='absolute inset-0 block'>
            <svg viewBox='0 0 400 400' preserveAspectRatio='xMidYMid slice' className='absolute inset-0 size-full' aria-hidden='true' focusable='false'>
                <defs>
                    <linearGradient id={`bg-${id}`} x1='0' y1='0' x2='0.4' y2='1'>
                        <stop offset='0%' stopColor={from} />
                        <stop offset='100%' stopColor={to} />
                    </linearGradient>
                    <radialGradient id={`halo-${id}`}>
                        <stop offset='0%' stopColor={accent} stopOpacity='0.45' />
                        <stop offset='100%' stopColor={accent} stopOpacity='0' />
                    </radialGradient>
                </defs>

                <rect width='400' height='400' fill={`url(#bg-${id})`} />
                <Motif variant={variant} accent={accent} gradientId={`halo-${id}`} />
            </svg>

            {label && (
                <span className='absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 pb-[7%]'>
                    <span className='block h-px w-1/5 bg-ivory/25' />
                    <span className='font-brand text-[clamp(0.45rem,2.6cqw,0.72rem)] uppercase tracking-[0.42em] text-ivory/70'>
                        Serene Corpse
                    </span>
                </span>
            )}
        </span>
    )
}

/** Cover artwork, or an on-brand placeholder until official artwork is uploaded. */
export const CoverArt: FC<CoverArtProps> = ({ src, alt, seed, className, sizes = '(min-width: 1024px) 25vw, 50vw', priority, label = true, grade = 'none' }): ReactNode => {
    return (
        <span className={cn('@container relative block overflow-hidden bg-charcoal', className)}>
            {src
                ? <Picture src={src} alt={alt} sizes={sizes} priority={priority} grade={grade} />
                : <Placeholder seed={seed} alt={alt} label={label} />}
        </span>
    )
}
